import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import {
	Droppable,
	type DroppablePayloadBase,
	getDraggableType,
	getDroppablePayload,
} from "@msuite/ui/dnd";
import { ColoredIcon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { TranslationKey } from "@msuite/utils/locales";
import type { Enums, Tables } from "@msuite/utils/types";
import React, { useMemo } from "react";
import { TicketCard } from "@/components/cards/ticket-card";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";

/** Props Interface */
interface ProjectTicketsStateContainerProps {
	tickets: Array<Tables<"tickets">>;
	state: Enums<"projectState">;
	defaultIsOpen: boolean;
}

export function ProjectTicketsStateContainer({
	tickets,
	defaultIsOpen,
	state,
}: ProjectTicketsStateContainerProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { updateIsDenied } = useTicketRules();

	const label: TranslationKey = `projects.state.${state}`;

	/** Memoized */
	const iconName = useMemo(() => {
		if (state === "active") return "circle";
		if (state === "inactive") return "circle-dashed";
		return "circle-dotted";
	}, [state]);

	const color = useMemo(() => {
		if (state === "active") return "emerald";
		if (state === "inactive") return "amber";
		return "red";
	}, [state]);

	const filteredTickets = useMemo(() => {
		if (!tickets) return [];
		return tickets.filter((ticket) => ticket.state === state);
	}, [tickets, state]);

	/** Functions */
	async function handleOnDrop(element: DroppablePayloadBase) {
		const type = getDraggableType(element);
		if (type === "ticket") {
			const { item } = getDroppablePayload<Tables<"tickets">>(element);
			await handleOnChangeTicketState(item);
		}
	}

	async function handleOnChangeTicketState(ticket: Tables<"tickets">) {
		const response = await client.tickets[":ticketId"].$patch({
			json: {
				...ticket,
				assignee: null,
				uploadResults: [],
				// `ZodNumberNullable` declares itself as a number while accepting and defaulting to
				// null, so the stored value passes through as it is.
				estimatedDays: ticket.estimatedDays as number,
				state,
			},
			param: {
				ticketId: ticket.id,
			},
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["tickets", ticket.id, ticket.projectId]),
		});
	}

	/** Render */
	return (
		<Droppable
			className="flex flex-col"
			accept={["ticket"]}
			handleOnDrop={handleOnDrop}
		>
			<ContentCard
				title={`${t(label)} (${filteredTickets.length})`}
				icon={() => (
					<ColoredIcon
						iconName={iconName}
						color={color}
					/>
				)}
				defaultIsOpen={defaultIsOpen}
			>
				<div className="-m-2">
					<React.Fragment>
						{!filteredTickets.length ? (
							<BlankSlate
								withAccessory={false}
								withBorder={false}
								withBackground={false}
							/>
						) : (
							<React.Fragment>
								{filteredTickets?.map((ticket) => (
									<TicketCard
										ticket={ticket}
										key={ticket.id}
										leftBorder="thick"
										// Dragging a card between these three containers is the state change and
										// nothing else, so a ticket the caller may not correct does not lift.
										isDraggable={!updateIsDenied(ticket)}
									/>
								))}
							</React.Fragment>
						)}
					</React.Fragment>
				</div>
			</ContentCard>
		</Droppable>
	);
}
